import { useEffect, useState, useCallback, JSX } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "@tanstack/react-store";
import { Menu, X, ChevronDown, Phone, Mail, ArrowRight } from "lucide-react";
import { appStore, toggleMobileMenu, closeMobileMenu } from "@/store";
import { cn } from "@/lib/utils";
import { navLinks, serviceLinks } from "@/lib/constants";

function ServicesDropdown({ open }: { open: boolean }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[520px] bg-white rounded-xl overflow-hidden"
        >
          <div className="grid grid-cols-2 gap-1 p-3">
            {serviceLinks.map((service) => (
              <Link
                key={service.href}
                to={service.href as never}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#6B1E2A]/5 group/item transition-colors"
              >
                <span className="text-sm font-medium text-[#1A1A1A] group-hover/item:text-[#6B1E2A] transition-colors">
                  {service.label}
                </span>
              </Link>
            ))}
          </div>
          <div className="p-3 bg-[#C9A84C]">
            <Link
              to="/services"
              className="flex items-center justify-center gap-2 w-full py-2 text-sm font-semibold text-[#6B1E2A] hover:text-[#4A1219] transition-colors"
            >
              View All Services →
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MobileDrawer({ open, pathname }: { open: boolean; pathname: string }) {
  const [servicesExpanded, setServicesExpanded] = useState(false);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setServicesExpanded(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          <motion.div
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 320,
              damping: 32,
              mass: 0.9,
            }}
            className="fixed top-0 right-0 bottom-0 z-50 w-[min(88vw,360px)] bg-white flex flex-col lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="flex items-center gap-2"
              >
                <img
                  src="/images/Rosewood Logo main version.png"
                  alt="Rosewood Tax Consultancy"
                  className="h-10 w-auto object-contain"
                />
                <div className="leading-none">
                  <div className="font-serif text-sm font-semibold text-[#4A1219] tracking-tight">
                    Rosewood
                  </div>
                  <div className="text-[8px] font-medium tracking-[0.15em] text-[#C9A84C] uppercase mt-0.5">
                    Consultancy
                  </div>
                </div>
              </Link>
              <button
                onClick={closeMobileMenu}
                className="p-2 rounded-lg text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F5F5F5] transition-colors"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto overscroll-contain py-3 px-3">
              {navLinks.map((link) => {
                const isActive =
                  link.href === "/"
                    ? pathname === "/"
                    : pathname.startsWith(link.href);

                if (!link.hasDropdown) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href as never}
                      className={cn(
                        "flex items-center px-3 py-3 rounded-xl text-[15px] font-medium transition-colors",
                        isActive
                          ? "text-[#6B1E2A] bg-[#6B1E2A]/6"
                          : "text-[#1A1A1A] hover:text-[#6B1E2A] hover:bg-[#6B1E2A]/5",
                      )}
                    >
                      {link.label}
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#6B1E2A]" />
                      )}
                    </Link>
                  );
                }

                return (
                  <div key={link.href}>
                    <button
                      onClick={() => setServicesExpanded((v) => !v)}
                      className={cn(
                        "w-full flex items-center px-3 py-3 rounded-xl text-[15px] font-medium transition-colors",
                        pathname.startsWith("/services")
                          ? "text-[#6B1E2A] bg-[#6B1E2A]/6"
                          : "text-[#1A1A1A] hover:text-[#6B1E2A] hover:bg-[#6B1E2A]/5",
                      )}
                      aria-expanded={servicesExpanded}
                    >
                      {link.label}
                      <ChevronDown
                        size={16}
                        className={cn(
                          "ml-auto transition-transform duration-200",
                          servicesExpanded && "rotate-180",
                        )}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {servicesExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            duration: 0.22,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="overflow-hidden"
                        >
                          <div className="ml-3 mt-0.5 mb-1 pl-3 border-l-2 border-[#E8E0D0] flex flex-col gap-0.5">
                            {serviceLinks.map((service) => (
                              <Link
                                key={service.href}
                                to={service.href as never}
                                className={cn(
                                  "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors group",
                                  pathname === service.href
                                    ? "text-[#6B1E2A] font-medium bg-[#6B1E2A]/5"
                                    : "text-[#4A4A4A] hover:text-[#6B1E2A] hover:bg-[#6B1E2A]/5",
                                )}
                              >
                                {service.label}
                                <ArrowRight
                                  size={13}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-[#6B1E2A]"
                                />
                              </Link>
                            ))}

                            <Link
                              to="/services"
                              className="flex items-center gap-1.5 px-3 py-2.5 mt-1 rounded-lg text-sm font-semibold text-[#6B1E2A] hover:bg-[#6B1E2A]/5 transition-colors"
                            >
                              View all services
                              <ArrowRight size={13} />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </nav>

            <div className="px-4 py-5 border-t border-[#E8E0D0] flex flex-col gap-3 bg-[#FAF7F2]">
              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold text-white bg-[#6B1E2A] rounded-xl hover:bg-[#4A1219] active:scale-[0.98] transition-all"
              >
                Get a Free Consultation
                <ArrowRight size={15} />
              </Link>

              <div className="flex items-center justify-center gap-5 text-sm text-[#6B6B6B]">
                <a
                  href="tel:+254703173405"
                  className="flex items-center gap-1.5 hover:text-[#6B1E2A] transition-colors"
                >
                  <Phone size={13} />
                  <span>+254 703 173405</span>
                </a>
                <a
                  href="mailto:info@rosewoodconsulting.co.ke"
                  className="flex items-center gap-1.5 hover:text-[#6B1E2A] transition-colors"
                >
                  <Mail size={13} />
                  <span>Email us</span>
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function Navbar(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const isMobileMenuOpen = useStore(appStore, (s) => s.isMobileMenuOpen);
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    closeMobileMenu();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setServicesOpen(false);
  }, [pathname]);

  return (
    <>
      <div className="bg-[#4A1219] text-white/80 text-xs py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a
              href="tel:+254703173405"
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Phone size={11} />
              <span>+254 703 173405</span>
            </a>
            <a
              href="mailto:info@rosewoodconsulting.co.ke"
              className="flex items-center gap-1.5 hover:text-white transition-colors"
            >
              <Mail size={11} />
              <span>info@rosewoodconsulting.co.ke</span>
            </a>
          </div>
          <span>Mon – Fri: 8:00 am – 5:30 pm</span>
        </div>
      </div>

      <motion.header
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300",
          scrolled ? "bg-white/98 backdrop-blur-md" : "bg-white",
        )}
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-18 py-3">
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/images/Rosewood Logo main version.png"
                alt="Rosewood Tax Consultancy"
                className="h-16 w-auto object-contain sm:h-20"
              />
              <div className="hidden sm:block leading-none">
                <div className="font-serif text-base font-semibold text-[#4A1219] leading-tight tracking-tight">
                  Rosewood
                </div>
                <div className="text-[9px] font-medium tracking-[0.15em] text-[#C9A84C] uppercase mt-0.5">
                  Consultancy
                </div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div
                    key={link.href}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <button
                      className={cn(
                        "flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-all",
                        pathname.startsWith("/services")
                          ? "text-[#6B1E2A] bg-[#6B1E2A]/5"
                          : "text-[#1A1A1A] hover:text-[#6B1E2A] hover:bg-[#6B1E2A]/5",
                      )}
                    >
                      {link.label}
                      <ChevronDown
                        size={14}
                        className={cn(
                          "transition-transform duration-200",
                          servicesOpen && "rotate-180",
                        )}
                      />
                    </button>
                    <ServicesDropdown open={servicesOpen} />
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href as never}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-md transition-all",
                      pathname === link.href
                        ? "text-[#6B1E2A] bg-[#6B1E2A]/5"
                        : "text-[#1A1A1A] hover:text-[#6B1E2A] hover:bg-[#6B1E2A]/5",
                    )}
                  >
                    {link.label}
                  </Link>
                ),
              )}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/contact"
                className="px-5 py-2.5 text-sm font-semibold text-white bg-[#6B1E2A] rounded-lg hover:bg-[#4A1219] transition-all active:scale-95"
              >
                Get Consultation
              </Link>
            </div>

            <button
              onClick={toggleMobileMenu}
              className={cn(
                "lg:hidden p-2 rounded-lg transition-colors",
                isMobileMenuOpen
                  ? "text-[#6B1E2A] bg-[#6B1E2A]/8"
                  : "text-[#1A1A1A] hover:bg-[#6B1E2A]/5",
              )}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </motion.div>
            </button>
          </div>
        </div>
      </motion.header>

      <MobileDrawer open={isMobileMenuOpen} pathname={pathname} />
    </>
  );
}
